import React from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { postMessageRN, runningInRNWebView, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { LinkButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import ANALYTICS_CATEGORIES from '../../../../analytics/categories';
import ANALYTICS_ROOT_PATHS from '../../../../analytics/paths';
import { useConfig } from '../../../../defaultConfig';
import type { AccountNumber, ReceiptType } from '../../../../generated-types';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useShowInstantPayment from '../../../../hooks/useShowInstantPayment';
import useTranslation from '../../../../hooks/useTranslation';
import { AnalyticsEvents, trackEvent, trackPageView } from '../../../../services/analytics';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { AmountBox, AmountInput } from '../../../AmountBox';
import { EfakturaLink } from '../../../EfakturaLink';
import { NoteField } from '../../../NoteField';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import { DateField } from '../../../shared/DateField';
import { FormFieldset } from '../../../shared/FormFieldset';
import { SelectList } from '../../../shared/SelectList';
import AccountField from '../../AccountField';
import FieldSkeleton from '../../FieldSkeleton';
import ReceiptField from '../Receipt/ReceiptField';
import ReceiverField from '../ReceiverField';
import { InstantPaymentCheckbox } from '../components';
import { PaymentActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import { isNoCoverage } from '../form/validate';
import FieldProps from './FieldProps';

const PaymentMobileFields: React.FC<FieldProps> = ({
    locked,
    shouldFocusFirstElement,
    freeWithdrawals,
    eFaktura,
    receiverState,
    kid,
    date,
    isFuturePayment,
    animateModalAs,
    hideInstant,
    paymentLimitsWarning,
    validAccounts,
    invalidAccounts,
    refetch,
    isLoading,
    error,
}) => {
    const { signingOperationPending } = useSigningContext();
    const { state, errors, handleChange, curryChange } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();
    const translated = useTranslation();
    const showInstantPaymentCheckbox = useShowInstantPayment([!hideInstant]);
    const { priceListInstantPaymentLink } = useConfig();

    return (
        <FormFieldset disabled={signingOperationPending}>
            <AmountBox
                isNoCoverage={isNoCoverage(state, validAccounts)}
                error={errors.amount}
                limitError={errors.amountLimit}
                limitWarning={paymentLimitsWarning}
                locked={locked}
            >
                <AmountInput
                    value={state.amount}
                    currency={state.currency}
                    ariaLabel={translated.payAmount}
                    onChange={curryChange<number | undefined>(PaymentActionType.AMOUNT)}
                    locked={locked}
                    focusFirst={shouldFocusFirstElement}
                />
            </AmountBox>
            {eFaktura.id && (
                <div className="e-faktura-fields__pdf-link">
                    <EfakturaLink eFakturaId={eFaktura.id} loading={eFaktura.loading} />
                </div>
            )}
            <SelectList>
                <li>
                    <AccountField
                        id="paymentFromAccount"
                        placeholder={translated.payFromAccount}
                        buttonLabel={translated.from}
                        title={translated.paymentFrom}
                        account={targetFromAccount}
                        accountNumber={state.fromAccount}
                        accounts={validAccounts ?? []}
                        invalidAccounts={invalidAccounts}
                        error={errors.accountId}
                        onChange={curryChange<AccountNumber | undefined>(PaymentActionType.FROM_ACCOUNT_SELECTED)}
                        disabled={signingOperationPending}
                        isLoading={isLoading}
                        loadingError={error}
                        refresh={refetch}
                        showModal={state.showFromAccounts}
                        setShowModal={(showModal: boolean) => {
                            trackPageView(`${ANALYTICS_ROOT_PATHS.PAYMENT}/select-account`);
                            trackEvent(AnalyticsEvents.PAYMENT, ANALYTICS_CATEGORIES.PAYMENT, 'select_from_account');
                            handleChange({
                                type: showModal
                                    ? PaymentActionType.SHOW_FROM_ACCOUNTS
                                    : PaymentActionType.HIDE_FROM_ACCOUNTS,
                            });
                        }}
                        locked={locked}
                        bottomLabel={
                            freeWithdrawals.show ? (
                                <AccountFreeWithdrawalLabel freeWithdrawals={freeWithdrawals.data} />
                            ) : undefined
                        }
                        animateModalAs={animateModalAs}
                    />
                </li>
                <li>
                    {receiverState.isLoading ? (
                        <FieldSkeleton />
                    ) : (
                        <ReceiverField
                            receiver={state.toAccount}
                            onChange={(selectedReceiver) => {
                                handleChange({
                                    type: PaymentActionType.TO_ACCOUNT_SELECTED,
                                    payload: selectedReceiver,
                                });
                            }}
                            error={errors.toAccount}
                            onShow={(show) => {
                                if (show) {
                                    trackPageView(`${ANALYTICS_ROOT_PATHS.PAYMENT}/select-receiver`);
                                    trackEvent(
                                        AnalyticsEvents.PAYMENT,
                                        ANALYTICS_CATEGORIES.PAYMENT,
                                        'select_to_account',
                                    );
                                }
                            }}
                            animateModalAs={animateModalAs}
                            locked={locked || receiverState.lock}
                            disabled={signingOperationPending}
                        />
                    )}
                </li>
                <li>
                    {receiverState.isLoading || kid.isLoading ? (
                        <FieldSkeleton isNoteField />
                    ) : (
                        <NoteField
                            note={state.note}
                            kid={state.kid?.value}
                            ownText={state.ownText}
                            selectedReceiver={state.toAccount}
                            error={errors.note || errors.kid}
                            onChange={curryChange<string>(PaymentActionType.NOTE)}
                            onChangeKid={curryChange(PaymentActionType.SET_KID)}
                            onChangeOwnText={curryChange<string>(PaymentActionType.OWNTEXT)}
                            onShow={(show) => {
                                if (show) {
                                    trackPageView(`${ANALYTICS_ROOT_PATHS.PAYMENT}/select-note`);
                                    trackEvent(AnalyticsEvents.PAYMENT, ANALYTICS_CATEGORIES.PAYMENT, 'select_note');
                                }
                            }}
                            animateModalAs={animateModalAs}
                            locked={locked || kid.lock}
                            disabled={signingOperationPending}
                            forceDisableKid={state.immediate}
                        />
                    )}
                </li>
                <li>
                    <DateField
                        label={translated.date}
                        minDate={date.minDate}
                        date={state.paymentDate ?? getZonedDate()}
                        paymentInterval={date.interval}
                        onChange={(date?: Date) =>
                            handleChange({
                                type: PaymentActionType.PAYMENT_DATE,
                                // https://jirasd.intra.eika.no/browse/NMBP-802
                                // pga. endring fra button til tekstfelt og hvis bruker manuelt oppgir
                                // ugyldig dato, blir "date" undefined.
                                // eslint-disable-next-line
                                // @ts-ignore
                                payload: date,
                            })
                        }
                        onShow={() => {
                            trackPageView(`${ANALYTICS_ROOT_PATHS.PAYMENT}/select-date`);
                            trackEvent(AnalyticsEvents.PAYMENT, ANALYTICS_CATEGORIES.PAYMENT, 'select_date');
                        }}
                        locked={locked}
                        disabled={signingOperationPending}
                        onlyBusinessDays
                        warning={errors.paymentDate}
                        immediate={state.immediate}
                        animateModalAs={animateModalAs}
                    />
                </li>
                {!isFuturePayment && (
                    <li>
                        <ReceiptField
                            onChange={curryChange<ReceiptType>(PaymentActionType.SET_RECEIPT_TYPE)}
                            receiptType={state.receiptType}
                            animateModalAs={animateModalAs}
                            locked={locked}
                        />
                    </li>
                )}
                {showInstantPaymentCheckbox && (
                    <InstantPaymentCheckbox
                        locked={locked}
                        onChange={curryChange<boolean>(PaymentActionType.SET_IMMEDIATE)}
                        error={errors.immediate}
                    />
                )}
                {state.immediate && priceListInstantPaymentLink?.url && runningInRNWebView && (
                    <LinkButton
                        className="link-price-button"
                        onClick={() =>
                            postMessageRN({
                                action: WebToRNAction.OPEN_BROWSER,
                                payload: `${window.location.origin}${priceListInstantPaymentLink.url}`,
                            })
                        }
                        icon={IconTypes.EKSTERN_24}
                        iconOnRightSide
                    >
                        {translated.immediatePayment.priceButton}
                    </LinkButton>
                )}
            </SelectList>
        </FormFieldset>
    );
};

export default PaymentMobileFields;
